import { useTranslation } from '@wix/yoshi-flow-editor';
import { useStyles } from '@wix/tpa-settings/react';
import { FeaturedType } from 'root/clientTypes';
import { FeaturedBadgePlacement } from 'root/consts';
import { stylesParams } from 'root/components/Menus/stylesParams';
import { LayoutStylesParams } from 'root/components/Menus/Settings/Tabs/LayoutTab/LayoutStylesParams';
import { Preset } from 'root/utils/presets';
import { getFeaturedTypeTranslationKey, isSupportedFeaturedType } from 'root/utils/featuredType';
import { useItemsContext } from 'root/contexts/ItemsContext';

/**
 * Only the grid layout puts the item image somewhere a badge can sit on it, so that is the
 * one layout offering the on-image placement — in the panel and in the widget alike.
 */
export const canPlaceFeaturedBadgeOnImage = (styles: ReturnType<typeof useStyles>) =>
  styles.get(LayoutStylesParams.preset) === Preset.Grid && styles.get(stylesParams.showItemImage);

export const useFeaturedBadgeLogic = ({ featuredType }: { featuredType?: FeaturedType }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const { itemBehavior } = useItemsContext();

  // Items highlighted before types existed (no type set) fall back to the generic
  // "Featured" label so they still show a badge.
  // Items with an unrecognised future type still show nothing.
  const effectiveFeaturedType = featuredType ?? FeaturedType.FEATURED;
  const label = isSupportedFeaturedType(effectiveFeaturedType)
    ? t(getFeaturedTypeTranslationKey(effectiveFeaturedType))
    : undefined;

  const canPlaceOnImage = itemBehavior?.canPlaceFeaturedBadgeOnImage ?? canPlaceFeaturedBadgeOnImage(styles);
  const featuredBadgePlacement =
    itemBehavior?.featuredBadgePlacement ?? (styles.get(stylesParams.featuredBadgePlacement) as FeaturedBadgePlacement);

  return {
    label,
    // On-image is offered only when the current item layout has an image area.
    isOnImage: canPlaceOnImage && featuredBadgePlacement === FeaturedBadgePlacement.OnImage,
    isVisible: !!label && styles.get(stylesParams.showFeaturedBadge),
  };
};
